/**
 * Hook: fetches the lightweight article index + category list.
 *
 * No Zustand store — these views don't share state with anything else and
 * the article browser is one route. Plain `useState` + `useEffect`.
 *
 * Exposes a `refetch` callback so editor mutations (create / update /
 * publish / unpublish / delete) can refresh the index without
 * remounting the layout. Without this, returning to the list after a
 * save showed stale data until a full reload.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import {
  ResearchArticleIndexItem,
  ResearchCategory,
} from "../../../models/Research";
import { useLeagueApi } from "../../../data/LeagueEnvironmentProvider";

export interface UseArticlesResult {
  loading: boolean;
  error: string | null;
  categories: ResearchCategory[];
  articles: ResearchArticleIndexItem[];
  /** Re-fetch categories + index. Safe to call from anywhere — the
   *  fetch is in-flight-cancellable so a fast double-tap won't race. */
  refetch: () => void;
}

export function useArticles(): UseArticlesResult {
  const api = useLeagueApi();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<ResearchCategory[]>([]);
  const [articles, setArticles] = useState<ResearchArticleIndexItem[]>([]);

  // A monotonic "current fetch id" that we increment on every fetch.
  // Each fetch's success/error handler only commits state if its id is
  // still the latest — this is how we cancel stale responses without
  // AbortController (the API client doesn't expose one). Refs avoid a
  // re-render on increment.
  const fetchIdRef = useRef(0);

  const refetch = useCallback(() => {
    const myId = ++fetchIdRef.current;
    setLoading(true);
    setError(null);

    Promise.all([
      api.getResearchCategories(),
      api.getResearchArticlesIndex(),
    ])
      .then(([catsResp, articlesResp]) => {
        if (fetchIdRef.current !== myId) return;
        setCategories(catsResp.data.categories ?? []);
        setArticles(articlesResp.data.articles ?? []);
        setLoading(false);
      })
      .catch((err) => {
        if (fetchIdRef.current !== myId) return;
        const msg =
          err?.response?.data?.detail ||
          err?.response?.data?.error ||
          err?.message ||
          "Failed to load articles";
        setError(msg);
        setLoading(false);
      });
  }, [api]);

  useEffect(() => {
    refetch();
    // Reset the fetch-id counter on unmount so any pending in-flight
    // responses can't fire a setState after teardown.
    return () => {
      fetchIdRef.current = -1;
    };
  }, [refetch]);

  return { loading, error, categories, articles, refetch };
}
